// (c) 2023 Acellera Ltd http://www.acellera.com
// All Rights Reserved
// No redistribution in whole or part
//
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { recursiveGetAllChildSystems } from "../3dViewer/stateTree";
import { numToHex } from "../3dViewer/Controls/utilsColors";
export const filterImages = (sentMessages) => {
    // Only one message can include images, so we keep only the latest.
    const lastMessageWithImage = (() => {
        for (let i = sentMessages.length - 1; i >= 0; i--) {
            if (sentMessages[i].content.some((item) => item.type === "image_url")) {
                return i;
            }
        }
        return -1;
    })();
    return sentMessages.map((message, index) => {
        const hasImageUrl = message.content.some((c) => c.type === "image_url");
        if (!hasImageUrl) {
            return message;
        }
        const isLatestWithImageUrl = index === lastMessageWithImage;
        return Object.assign(Object.assign({}, message), { content: message.content.filter((item) => item.type !== "image_url" || isLatestWithImageUrl) });
    });
};
export function filterSystData(sentMessages) {
    // Remove the text element with systems context of all user messages except last one
    return sentMessages.map((message, index) => {
        if (message.role !== "user")
            return message;
        const content = message.content;
        return Object.assign(Object.assign({}, message), { content: content
                .filter((c) => c.type !== "text" ||
                !c.is_viewer_state ||
                index + 1 === sentMessages.length)
                .map((c) => {
                if (c.is_viewer_state) {
                    const { is_viewer_state } = c, rest = __rest(c, ["is_viewer_state"]);
                    return rest;
                }
                return c;
            }) });
    });
}
export function filterSentMessages(sentMessages) {
    const sentMessagesImgFiltPre = filterImages(sentMessages);
    const sentMessagesImgFilt = filterSystData(sentMessagesImgFiltPre);
    const sendMessagesFeedbackFilt = sentMessagesImgFilt.map((message) => {
        const { feedback, id } = message, messageWithoutFeedback = __rest(message, ["feedback", "id"]);
        return messageWithoutFeedback;
    });
    return sendMessagesFeedbackFilt;
}
export function loadImage(selectedFile) {
    return new Promise((resolve, reject) => {
        // reader.readAsText(fileContent);
        const reader = new FileReader();
        reader.readAsDataURL(selectedFile); // This reads the file as a data URL (base64 encoded)
        reader.onload = () => __awaiter(this, void 0, void 0, function* () {
            const base64Image = reader.result; // e.g., "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQ..."
            resolve(base64Image); // Resolve with the base64 image data
        });
        reader.onerror = (error) => {
            console.error("Error reading file:", error);
        };
    });
}
export function getSystemsContext(vss) {
    const systems = vss.getState().loaded_structures;
    const allSystemsData = [];
    systems.forEach((systNode) => {
        const systems = recursiveGetAllChildSystems(systNode);
        systems.forEach((s) => {
            var _a;
            const representations = (_a = s.representations) === null || _a === void 0 ? void 0 : _a.map((r) => {
                const nr = {
                    opacity: r.alpha,
                    color: r.color,
                    sel: r.selection,
                    style: r.type,
                    visible: r.visibility === undefined ? true : r.visibility,
                };
                if (r.color === "uniform" && r.color_value) {
                    const hexval = numToHex(r.color_value);
                    if (hexval) {
                        nr.color = hexval;
                    }
                }
                return nr;
            });
            const d = {
                name: s.name,
                id: s.moleculeID,
                representations: representations,
                visible: s.visibility === undefined ? true : s.visibility,
            };
            allSystemsData.push(d);
        });
    });
    return allSystemsData;
}
export const viewerScreenshotToFile = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const molstar = window.molstar;
    if (!molstar)
        return;
    const imageDataUri = yield ((_a = molstar.helpers.viewportScreenshot) === null || _a === void 0 ? void 0 : _a.getImageDataUri());
    if (!imageDataUri)
        return;
    const imageBlob = yield fetch(imageDataUri).then((res) => res.blob());
    const imageFile = new File([imageBlob], "screenshot", {
        type: "image/png",
    });
    return imageFile;
});
export const prepareMessageContent = (input, vss) => __awaiter(void 0, void 0, void 0, function* () {
    const newContent = [];
    const systsData = getSystemsContext(vss);
    if (systsData.length > 0) {
        const viewerScreenshot = yield viewerScreenshotToFile();
        if (viewerScreenshot) {
            const fileContent = (yield loadImage(viewerScreenshot));
            if (fileContent) {
                newContent.push({
                    type: "image_url",
                    image_url: {
                        url: fileContent,
                    },
                });
            }
        }
        newContent.push({
            type: "text",
            text: `<viewer_state>${JSON.stringify(systsData)}</viewer_state>\n`,
            is_viewer_state: true,
        });
    }
    const textContent = { type: "text", text: input };
    newContent.push(textContent);
    // if (system) {
    //   newContent.push({
    //     type: "text",
    //     text: JSON.stringify(system),
    //   });
    return newContent;
});
